import React, { useState, useContext } from 'react'
import MaterialDrawer from '@material-ui/core/Drawer'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import List from '@material-ui/core/List'
import {
  Bell,
  CirclePercent,
  Download,
  Home,
  Layers,
  List as ListIcon,
  LucideIcon,
  ParkingCircle,
  Settings,
  Sparkles,
  Store,
  TabletSmartphone,
  Tag,
  Trophy,
  User,
  Users,
} from 'lib/lucide'
import { NavLink } from 'react-router-dom'

import { UserContext } from 'contexts/UserContext'
import { Version } from 'components/Version'

import { useStyles } from './styles'

interface Path {
  pathname: string
  search?: string
  hash?: string
  state?: Record<string, unknown>
}

interface DrawerOption {
  label: string
  link?: string | Path
  icon: LucideIcon
  roles?: string[]
  showCondition?: boolean
}

export const drawerOptions: DrawerOption[] = [
  {
    label: 'Dashboard',
    link: {
      pathname: '/dashboard',
    },
    icon: Home,
    roles: ['admin'],
  },
  {
    label: 'Usuarios',
    link: '/users',
    icon: User,
    roles: ['admin'],
  },
  {
    label: 'Marcas',
    link: { pathname: '/stores' },
    icon: ListIcon,
    roles: ['admin', 'adminLimited', 'marketing'],
  },
  {
    label: 'Niveles',
    link: { pathname: '/memberships' },
    icon: Layers,
    roles: ['admin'],
  },
  {
    label: 'Promos',
    link: { pathname: '/coupons' },
    icon: CirclePercent,
    roles: ['admin', 'adminLimited', 'marketing'],
  },
  {
    label: 'Rewards manuales',
    link: { pathname: '/rewards-manuales' },
    icon: Store,
    roles: ['admin', 'adminLimited', 'marketing'],
  },
  {
    label: 'Push notification',
    link: { pathname: '/notifications' },
    icon: Bell,
    roles: ['admin', 'adminLimited'],
  },
  {
    label: 'Automatización',
    link: { pathname: '/notificaciones/automaticas' },
    icon: Sparkles,
    roles: ['admin'],
  },
  {
    label: 'Tags',
    link: { pathname: '/tags' },
    icon: Tag,
    roles: ['admin'],
  },
  {
    label: 'User Test',
    link: { pathname: '/user-test' },
    icon: TabletSmartphone,
    roles: ['admin'],
  },
  {
    label: 'Sorteos',
    link: { pathname: '/events' },
    icon: Trophy,
    roles: ['admin'],
  },
  {
    label: 'Parking',
    link: { pathname: '/parking-configs' },
    icon: ParkingCircle,
    roles: ['admin'],
  },
  {
    label: 'Administradores',
    link: { pathname: '/admins' },
    icon: Users,
    roles: ['admin'],
  },
  {
    label: 'Descargas',
    link: { pathname: '/downloads' },
    icon: Download,
    roles: ['admin', 'adminLimited', 'marketing', 'analyst'],
  },
  {
    label: 'Config. de App',
    link: { pathname: '/app-config' },
    icon: Settings,
    roles: ['admin'],
  },
]

interface DrawerProps {
  onExpandChange?: (expanded: boolean) => void
}

const Drawer: React.FC<DrawerProps> = ({ onExpandChange }) => {
  const classes = useStyles()
  const [isExpanded, setIsExpanded] = useState(false)
  const { admin } = useContext(UserContext)
  const userRoles = admin?.roles.map(role => role.name) || []

  return (
    <MaterialDrawer
      variant="permanent"
      className={`${classes.drawer} ${!isExpanded ? classes.drawerCollapsed : ''}`}
      classes={{
        paper: `${classes.drawerPaper} ${!isExpanded ? classes.drawerPaperCollapsed : ''}`,
      }}
      onMouseEnter={() => {
        setIsExpanded(true)
        onExpandChange?.(true)
      }}
      onMouseLeave={() => {
        setIsExpanded(false)
        onExpandChange?.(false)
      }}
    >
      <div className={classes.toolbar} />
      <List className={classes.list}>
        {drawerOptions
          .filter(
            option =>
              (option.roles ? option.roles.some(role => userRoles.includes(role)) : true) &&
              (option.showCondition ?? true),
          )
          .map(option => (
            <NavLink
              exact
              to={typeof option.link === 'string' ? option.link : option.link?.pathname || ''}
              activeClassName={classes.activeLink}
              className={classes.link}
              key={option.label}
            >
              <ListItem button>
                <ListItemIcon
                  className={`${classes.listItemIcon} ${!isExpanded ? classes.icon : ''}`}
                >
                  <option.icon size={22} />
                </ListItemIcon>
                {isExpanded && (
                  <ListItemText primary={option.label} classes={{ primary: classes.menuLabel }} />
                )}
              </ListItem>
            </NavLink>
          ))}
      </List>
      {isExpanded && <Version />}
    </MaterialDrawer>
  )
}

export { Drawer }
